import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import {
	User,
	signInWithEmailAndPassword,
	setPersistence,
	browserSessionPersistence,
	onAuthStateChanged,
	createUserWithEmailAndPassword,
	sendPasswordResetEmail,
} from "firebase/auth";
import { useFirebase } from "./FirebaseProvider";

import { doc, getDoc } from "firebase/firestore";
import { useNetwork } from "./NetworkProvider";
import { useNavigate } from "react-router-dom";
import { AuthProviderData } from "../types/providers/AuthenticationProvider";
import { SubscritpionDetail, UserType } from "../types/user";
import { TraderType } from "../types/Trader/Trader";
import { useSnackbar } from "./SnackbarProvider";
const AuthContext = createContext<AuthProviderData>({} as AuthProviderData);

/**
 * This Provider handle the authentication process
 *		- Authenticate the user and boat
 *		- Check if the user is admin
 *		- Get the corresponding Information
 * 		- Based on his it will redirect it to the corresponding page
 */
export const AuthProvider = ({ children }: { children: JSX.Element }) => {
	const { myAuth, myFS } = useFirebase();

	const [user, setUser] = useState<User | null>(null);
	const [profile, setProfile] = useState<UserType | null>(null);
	const [traderProfile, setTraderProfile] = useState<TraderType | null>(null);
	const [authInitialized, setAuthInitialized] = useState(false);
	const [profileInitialized, setProfileInitialized] =
		useState<boolean>(false);
	const [userSubscription, setUserSubscription] =
		useState<SubscritpionDetail | null>(null);
	const [temporaryPassword, setTemporaryPassword] = useState<string | null>(
		null
	);
	const navigate = useNavigate();
	const { openLoadingSnackbar, closeLoadingSnackbar } = useSnackbar();
	const { makePost } = useNetwork();

	async function getUserInfo(uid?: string) {
		let isUserTrader = false;
		let isUserAffiliate = false;
		if (uid != null) {
			try {
				const profile = await getUserInformation(uid);
				const traderProfile = await getTraderInformation(uid);
				setProfile(profile);
				setTraderProfile(traderProfile);
				isUserTrader = traderProfile != null;
				isUserAffiliate =
					profile == null
						? false
						: profile.role == "Affiliate" ||
						  profile.role == "AffiliateMaster";
			} catch (err) {
				console.log(err);
			}
		}
		setProfileInitialized(true);
		return { isUserTrader, isUserAffiliate };
	}

	const getUserInformation = async (uid: string) => {
		let user: UserType | null = null;
		try {
			let docRef = doc(myFS, "users", uid);
			const docSnap = await getDoc(docRef);
			if (docSnap.exists()) {
				user = { ...docSnap.data(), uid: uid } as UserType;
			}
		} catch (err) {
			console.log(err);
		}
		return user;
	};
	const getTraderInformation = async (uid: string) => {
		let trader: TraderType | null = null;
		try {
			let docRef = doc(myFS, "traders", uid);
			const docSnap = await getDoc(docRef);
			if (docSnap.exists()) {
				trader = { ...docSnap.data(), uid: uid } as TraderType;
			}
		} catch (err) {
			console.log(err);
		}
		return trader;
	};

	useEffect(() => {
		if (myAuth) {
			let unsubscribe = onAuthStateChanged(myAuth, async (user) => {
				await getUserInfo(user?.uid);
				if (user != null) {
					setUser(user);
				}
				setAuthInitialized(true);
			});
			return () => unsubscribe();
		}
	}, [myAuth]);
	useEffect(() => {
		if (user == null) return;
		setAuthInitialized(true);
		(async () => {
			await getUserInfo(user.uid);
		})();
	}, [user]);
	const handleRefreshUserSubscription = useCallback(async () => {
		if (profile?.purchaseTokens) {
			await refreshUserSubscription();
		}
	}, [profile?.purchaseTokens]);
	useEffect(() => {
		// Check if user exists and has purchaseTokens
		if (profile?.purchaseTokens) {
			// handleRefreshUserSubscription();
			refreshUserSubscription();
		}
	}, [profile]);

	const loginWithEmailAndPassword = async (
		email: string,
		password: string
	): Promise<void> => {
		try {
			openLoadingSnackbar();
			await setPersistence(myAuth, browserSessionPersistence);

			let userCredential = await signInWithEmailAndPassword(
				myAuth,
				email,
				password
			);
			let user = userCredential.user;

			setUser(user);
			const userRole = await getUserInfo(user.uid);
			navigate("/reserved/dashboard");
			// if (userRole.isUserTrader) {
			// 	navigate("/trader/profile");
			// } else if (userRole.isUserAffiliate) {
			// 	navigate("/affiliate/dashboard");
			// } else {
			// }
		} catch (err) {
			closeLoadingSnackbar();
			console.error(err);
			const error = err as Error;
			if (error.message.includes("auth/wrong-password"))
				throw new Error("Wrong password");
			if (error.message.includes("auth/user-not-found"))
				throw new Error("No user with this email");
			if (error.message.includes("auth/invalid-credential"))
				throw new Error("Credenziali invalide");

			throw new Error("Something went wrong");
		}
		closeLoadingSnackbar();
	};

	const signWithEmailAndPassword = async (
		email: string,
		password: string,
		navigateToDashboard?: boolean
	): Promise<string> => {
		let uid = "";
		try {
			await setPersistence(myAuth, browserSessionPersistence);
			openLoadingSnackbar();
			let userCredential = await createUserWithEmailAndPassword(
				myAuth,
				email,
				password
			);
			let user = userCredential.user;
			uid = user.uid;
			const tokenId = await user.getIdToken();
			const response = await makePost("signup", {
				uid: user.uid,
				tokenId,
			});
			setUser(user);
			await getUserInfo(user.uid);
			if (navigateToDashboard == null || navigateToDashboard)
				navigate("/reserved/dashboard");
		} catch (err) {
			closeLoadingSnackbar();
			console.error(err);
			const error = err as Error;
			throw new Error("Something went wrong");
		}
		closeLoadingSnackbar();
		return uid;
	};
	const recoverPassword = async (email?: string) => {
		if (email) {
			await sendPasswordResetEmail(myAuth, email);
		} else {
			if (user == null || user.email == null) return;
			await sendPasswordResetEmail(myAuth, user.email);
		}
	};
	const logout = async () => {
		setUser(null);
		setProfile(null);
		setTraderProfile(null);
		await myAuth.signOut();
	};
	const refreshUserSubscription = async () => {
		if (user == null) return;
		try {
			console.log("hello");
			const tokenId = await user.getIdToken();

			const userSubscriptionRaw = await makePost(
				"users/getUserSubscription",
				{},
				tokenId
			);
			console.log(userSubscriptionRaw.status);
			console.log(userSubscriptionRaw.data);
			if (userSubscriptionRaw.status === 200) {
				setUserSubscription(userSubscriptionRaw.data.subscription);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const authContextData: AuthProviderData = {
		authInitialized,
		profileInitialized,
		loginWithEmailAndPassword,
		signWithEmailAndPassword,
		user,
		logout,
		traderProfile,
		setTraderProfile,
		getTraderInformation,
		profile,
		userSubscription,
		recoverPassword,
		setTemporaryPassword,
		temporaryPassword,
	};
	return (
		<AuthContext.Provider value={authContextData}>
			{children}
		</AuthContext.Provider>
	);
};
export const useAuth = () => useContext(AuthContext);
